// src/features/admin/adminUserListApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

interface AdminUser {
  id: number;
  full_name: string;
  user_name: string;        // ✅ matches backend DB column name
  email: string;
  contact_no: string;
  role: string;
  status: number;
  createddt: string;
  updateddt: string | null;
}

interface ListAdminUsersRequest {
  role?: string;
  _ts?: number;             // ✅ cache buster — not sent to server, only changes query key
}

interface ListAdminUsersResponse {
  error: boolean;
  message: string;
  data: AdminUser[];
}

interface UpdateAdminUserRequest {
  id: number;
  full_name: string;
  user_name: string;        // ✅ matches backend DB column name
  email: string;
  contact_no: string;
  role: string;
  status: number;
  password?: string;
}

interface UpdateAdminUserResponse {
  error: boolean;
  message: string;
  data?: AdminUser;
}

export const adminUserListApi = createApi({
  reducerPath: 'adminUserListApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      return headers;
    },
  }),
  tagTypes: ['AdminUser'],
  keepUnusedDataFor: 0, // ✅ Never cache — always fetch fresh data
  endpoints: (build) => ({

    listAdminUsers: build.query<ListAdminUsersResponse, ListAdminUsersRequest>({
      query: (params) => {
        const formData = new URLSearchParams();
        if (params.role && params.role.trim() !== '') {
          formData.append('role', params.role);
        }
        // _ts is only used as RTK Query cache key, not sent to server
        return {
          url: 'admin/adminlistusers',
          method: 'POST',
          body: formData.toString(),
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        };
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'AdminUser' as const, id })),
              { type: 'AdminUser', id: 'LIST' },
            ]
          : [{ type: 'AdminUser', id: 'LIST' }],
      transformResponse: (response: any) => response,
    }),

    updateAdminUser: build.mutation<UpdateAdminUserResponse, UpdateAdminUserRequest>({
      query: (userData) => {
        // ✅ Send as JSON so id and user_name reach server correctly
        const body: Record<string, any> = {
          id: Number(userData.id),
          full_name: userData.full_name.trim(),
          user_name: userData.user_name.trim(), // ✅ backend DB column name
          email: userData.email.trim(),
          contact_no: userData.contact_no.trim(),
          role: userData.role,
          status: Number(userData.status),
        };

        if (userData.password && userData.password.trim() !== '') {
          body.password = userData.password;
          body.confirm_password = userData.password;
        }

        return {
          url: 'admin/update-adminuser',
          method: 'POST',
          body: JSON.stringify(body),
          headers: { 'Content-Type': 'application/json' },
        };
      },
      // ✅ Invalidate specific user + LIST so refetch triggers correctly
      invalidatesTags: (result, error, arg) => [
        { type: 'AdminUser', id: arg.id },
        { type: 'AdminUser', id: 'LIST' },
      ],
      transformResponse: (response: any) => response,
    }),

  }),
});

export const { useListAdminUsersQuery, useUpdateAdminUserMutation } = adminUserListApi;